import React from 'react';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { ZigzagStrip } from '../../Themes/Brand';
import { confirmDiscardChanges, useUnsavedChangesGuard } from '../../Hooks/useUnsavedChangesGuard';

interface FormShellProps {
    title: string;
    icon: IconDefinition;
    accentColor: string;
    backHref?: string;
    /** Renders a button instead of a link when back is an in-page action. */
    onBack?: () => void;
    /** Optional header actions on the right side. */
    actions?: React.ReactNode;
    /** When true, warns before losing unsaved changes via back/browser-back/refresh. */
    isDirty?: boolean;
    children: React.ReactNode;
}

const FormShell: React.FC<FormShellProps> = ({ title, icon, accentColor, backHref, onBack, actions, isDirty = false, children }) => {
    useUnsavedChangesGuard(isDirty);

    return (
        <Container fluid="lg" className="py-4">
        <div style={{
            backgroundColor: 'var(--st-paper)',
            borderRadius: 'var(--st-radius-card)',
            boxShadow: 'var(--st-shadow-card)',
            overflow: 'hidden',
        }}>
            <ZigzagStrip color={accentColor} />

            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1.1rem 1.5rem',
                borderBottom: '1px solid var(--st-line-soft)',
            }}>
                {onBack ? (
                    <button
                        type="button"
                        onClick={() => { if (!isDirty || confirmDiscardChanges()) onBack(); }}
                        title="Späť"
                        className="st-btn st-btn--outline st-btn--icon st-btn--sm"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.72rem' }} />
                    </button>
                ) : (
                    <Link
                        to={backHref ?? '/home'}
                        onClick={e => { if (isDirty && !confirmDiscardChanges()) e.preventDefault(); }}
                        title="Späť"
                        className="st-btn st-btn--outline st-btn--icon st-btn--sm"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '0.72rem' }} />
                    </Link>
                )}
                <div style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    backgroundColor: accentColor + '1A',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <FontAwesomeIcon icon={icon} style={{ color: accentColor, fontSize: '1rem' }} />
                </div>
                <h2 style={{
                    fontFamily: 'var(--st-font-display)',
                    fontWeight: 700,
                    fontSize: '1.08rem',
                    color: 'var(--st-text)',
                    margin: 0,
                    flex: 1,
                }}>
                    {title}
                </h2>
                {actions}
            </div>

            <div style={{ padding: '1.75rem 1.5rem' }}>
                {children}
            </div>
        </div>
    </Container>
    );
};

export default FormShell;
